// Sales rep autocomplete — suggests reps the owner has used before for
// the currently-selected broker. Picking a suggestion fills both the
// name and (if blank) the phone field. The owner can still type a
// brand-new rep name; no match = free text passes through to the form.
//
// Sources reps from past jobs (`salesRepName` + `salesRepPhone` fields).
// The `useKnownReps(companyId)` hook loads + dedupes that roster once
// per modal/screen open and returns the array.
//
// Usage:
//   const knownReps = useKnownReps(companyId);
//   <SalesRepAutocomplete
//     value={form.salesRepName}
//     brokerId={form.brokerId}
//     knownReps={knownReps}
//     onPick={(rep) => setForm(f => ({ ...f, salesRepName: rep.name, salesRepPhone: rep.phone || f.salesRepPhone || "" }))}
//     onChange={(v) => set("salesRepName", v)}
//   />

import React, { useState, useEffect, useRef } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Loads the roster of past reps for a company. Each entry:
 *   { brokerId, name, phone, lastSeen }
 *
 * Deduped by `${brokerId}::${name.toLowerCase()}` so each broker has its
 * own roster (DAI's Amanda is a different person from Malca's Amanda
 * even if they share a first name). Most recent phone wins on duplicates
 * so a rep who changed numbers eventually surfaces the new one.
 *
 * Sorted by lastSeen desc so the most-used reps surface first when the
 * autocomplete shows the empty-query roster on focus.
 */
export function useKnownReps(companyId) {
  const [reps, setReps] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "jobs"),
          where("companyId", "==", companyId),
        ));
        if (cancelled) return;
        const repMap = new Map();
        for (const d of snap.docs) {
          const j = d.data();
          const name = (j.salesRepName || "").trim();
          if (!name) continue;
          const key = `${j.brokerId || ""}::${name.toLowerCase()}`;
          const created = j.createdAt?.toDate?.()?.getTime?.() || 0;
          const cur = repMap.get(key);
          if (!cur || created > cur.lastSeen) {
            repMap.set(key, {
              brokerId: j.brokerId || "",
              name,
              phone: j.salesRepPhone || cur?.phone || "",
              lastSeen: created,
            });
          }
        }
        setReps([...repMap.values()].sort((a, b) => b.lastSeen - a.lastSeen));
      } catch (err) {
        // Silent — autocomplete just stays empty if we can't load.
        console.warn("Couldn't load known reps", err);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);
  return reps;
}

export default function SalesRepAutocomplete({ value, brokerId, knownReps, onPick, onChange, style }) {
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapRef = useRef(null);

  // Close the popover on outside click. Without this, scrolling the form
  // and clicking elsewhere would leave the suggestions stuck open.
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Filter the roster: same broker first, then substring match on what
  // they've typed. Empty input shows the full broker roster so the owner
  // can browse without remembering a name.
  const suggestions = (() => {
    const v = (value || "").trim().toLowerCase();
    const sameBroker = (knownReps || []).filter((r) => r.brokerId === brokerId && r.brokerId);
    const filtered = v
      ? sameBroker.filter((r) => r.name.toLowerCase().includes(v))
      : sameBroker;
    // De-duplicate by lowercased name, preferring entries that already
    // have a phone number on file.
    const seen = new Map();
    for (const r of filtered) {
      const key = r.name.toLowerCase();
      const cur = seen.get(key);
      if (!cur || (!cur.phone && r.phone)) seen.set(key, r);
    }
    return [...seen.values()].slice(0, 6);
  })();

  function commit(rep) {
    onPick(rep);
    setOpen(false);
    setHighlightIdx(-1);
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", flex: 1 }}>
      <input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlightIdx(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || suggestions.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIdx((i) => Math.min(i + 1, suggestions.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIdx((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" && highlightIdx >= 0) {
            e.preventDefault();
            commit(suggestions[highlightIdx]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder="e.g. Amanda Connelley"
        autoComplete="off"
        style={{ width: "100%", ...(style || {}) }}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          marginTop: 4, background: "#fff",
          border: "1px solid #ddd", borderRadius: 7,
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          zIndex: 50, maxHeight: 220, overflowY: "auto",
        }}>
          {suggestions.map((r, i) => (
            <div
              key={`${r.brokerId}::${r.name}`}
              // onMouseDown (not onClick) so the input's onBlur — which is
              // what would fire first on click — doesn't close the popover
              // before the selection registers.
              onMouseDown={(e) => { e.preventDefault(); commit(r); }}
              onMouseEnter={() => setHighlightIdx(i)}
              style={{
                padding: "8px 10px", fontSize: 12, cursor: "pointer",
                background: i === highlightIdx ? "#f0f6ff" : "transparent",
                borderBottom: i < suggestions.length - 1 ? "1px solid #f0f0f0" : "none",
                display: "flex", justifyContent: "space-between", gap: 10,
              }}
            >
              <span style={{ fontWeight: 600, color: "#222" }}>{r.name}</span>
              {r.phone && (
                <span style={{ color: "#888", fontSize: 11 }}>{r.phone}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
